import { type LicenseGateState, useLicense } from "@coach/license-client";
import { Glass } from "@coach/ui";
import { type FormEvent, useEffect, useState } from "react";
import { api, apiFetch } from "../lib/api";
import { authClient, signIn } from "../lib/auth-client";
import { getOrCreateDeviceId } from "../lib/device-id";
import {
  forgetKnownAccount,
  type KnownAccount,
  listKnownAccounts,
  recordKnownAccount,
} from "../lib/known-accounts";
import { AccountAvatar } from "./app-header/account-avatar";
import { BlockedScreen } from "./blocked-screen";

interface OrgSummary {
  id: string;
  name: string;
  slug: string;
  logo: string | null;
  role: string;
  ownerEmail?: string | null;
}

interface MeResponse {
  user: {
    id: string;
    email: string;
    name: string | null;
    image: string | null;
  };
  organization: OrgSummary | null;
  organizations: OrgSummary[];
}

/**
 * Full-screen gate shown when no valid license is in cache.
 *
 * Flow:
 *   1. User signs in (or creates an account) with email/password — same
 *      credentials as the web dashboard. Auth uses a session cookie set by
 *      the Hono server.
 *   2. App immediately calls `POST /api/v1/license/issue` (cookie-authed),
 *      receives an Ed25519-signed JWT bound to the account's organization,
 *      and caches it via `useLicense().setToken()`.
 *   3. Gate unmounts, app boots.
 *
 * Licenses are account-bound, NOT device-bound — sign in on as many devices
 * as you like.
 */
export function AuthGate({ state }: { state: LicenseGateState }) {
  const { setToken } = useLicense();
  // The app is sign-in only. Account creation happens on the landing
  // dashboard — coaches need to finish onboarding (club, plan, etc.) before
  // a license can be issued, and that flow lives on the web.
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Set when sign-in succeeded but the org has no usable license (trial
  // exhausted / subscription lapsed). We render the paywall instead of an
  // inline error so the coach can recover via the Stripe portal.
  const [blockReason, setBlockReason] = useState<string | null>(null);
  const [blockOrgRole, setBlockOrgRole] = useState<string | null>(null);
  const [blockOwnerEmail, setBlockOwnerEmail] = useState<string | null>(null);
  const [accounts, setAccounts] = useState<KnownAccount[]>(() => listKnownAccounts());
  // `picker` is the default landing surface when there are known accounts.
  // Setting it to false reveals the email/password form.
  const [picker, setPicker] = useState<boolean>(() => accounts.length > 0);
  // When a coach picks a known account we lock the email field so they only
  // need to re-enter their password (decision B — never persist passwords).
  const [lockedEmail, setLockedEmail] = useState<string | null>(null);

  // If the gate re-renders due to a `blocked` state (e.g. token expired) we
  // want the picker to show so the user can simply re-auth with one click.
  useEffect(() => {
    if (state.kind === "blocked" && accounts.length > 0) {
      setPicker(true);
    }
  }, [state.kind, accounts.length]);

  function pickAccount(account: KnownAccount): void {
    setLockedEmail(account.email);
    setEmail(account.email);
    setPassword("");
    setErr(null);
    setPicker(false);
  }

  function useAnotherAccount(): void {
    setLockedEmail(null);
    setEmail("");
    setPassword("");
    setErr(null);
    setPicker(false);
  }

  function backToPicker(): void {
    setLockedEmail(null);
    setErr(null);
    setPicker(true);
  }

  // When the signed-in user belongs to multiple clubs we show an inline
  // chooser before issuing the license. `null` means "no choice needed yet".
  const [orgChoice, setOrgChoice] = useState<OrgSummary[] | null>(null);

  function onForget(account: KnownAccount): void {
    forgetKnownAccount(account.email);
    const remaining = listKnownAccounts();
    setAccounts(remaining);
    if (remaining.length === 0) setPicker(false);
  }

  /**
   * Finalise sign-in: pulls /me, lets multi-club coaches pick an org, then
   * issues the license. Persists the chosen account into known-accounts so
   * the picker can offer it next time.
   */
  async function finalize(targetOrgId: string | null): Promise<void> {
    let me: MeResponse | null = null;
    try {
      me = await apiFetch<MeResponse>("/api/v1/me", {
        credentials: "include",
      });
    } catch {
      // /me failure is fatal — without an org list we can't issue a license
      // sensibly. Surface the underlying issue via license.issue() below.
    }

    // No memberships → tell the user to onboard via the dashboard.
    if (me && me.organizations.length === 0) {
      setErr(
        "Dieses Konto hat noch keinen Verein. Öffne das Dashboard, um das Onboarding abzuschließen.",
      );
      return;
    }

    // Multi-club + no choice made → render the picker. We DON'T call setActive
    // until the user picks; that way an accidental Cancel doesn't change
    // server-side state.
    if (me && me.organizations.length > 1 && !targetOrgId && !me.organization?.id) {
      setOrgChoice(me.organizations);
      return;
    }

    const orgId = targetOrgId ?? me?.organization?.id ?? me?.organizations[0]?.id ?? null;

    // Best-effort: mirror the choice into the session so the dashboard +
    // future /me calls agree on the active club. Silent failure is fine —
    // the license JWT carries the canonical orgId anyway.
    if (orgId) {
      try {
        await authClient.organization.setActive({ organizationId: orgId });
      } catch {
        /* ignore */
      }
    }

    const deviceId = getOrCreateDeviceId();
    const issued = await api().license.issue({
      orgId: orgId ?? undefined,
      ...(deviceId ? { deviceId } : {}),
    });
    if (!issued.ok) {
      // Billing-related rejections mean the account is fine but the org has
      // no usable license. Show the paywall (Stripe portal) rather than a
      // raw error line on the sign-in form.
      if (BLOCKING_REASONS.has(issued.reason)) {
        const activeOrg = me?.organizations.find((o) => o.id === orgId) ?? me?.organization ?? null;
        setBlockOrgRole(activeOrg?.role ?? null);
        setBlockOwnerEmail(activeOrg?.ownerEmail ?? null);
        setBlockReason(issued.reason);
        return;
      }
      setErr(humanize(issued.reason));
      return;
    }
    await setToken(issued.token);

    const chosen = me?.organizations.find((o) => o.id === orgId) ?? me?.organization ?? null;

    // Persist the account hint so the picker can offer it next time.
    if (me) {
      recordKnownAccount({
        email: me.user.email,
        name: me.user.name,
        image: me.user.image ?? null,
        orgName: chosen?.name ?? null,
        orgId: chosen?.id ?? null,
      });
    } else {
      recordKnownAccount({
        email,
        name: null,
        image: null,
        orgName: null,
        orgId: null,
      });
    }
    setAccounts(listKnownAccounts());
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    setBusy(true);
    try {
      const r = await signIn.email({ email, password });
      if (r.error) throw new Error(r.error.message ?? "sign-in failed");
      await finalize(null);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function onPickOrg(org: OrgSummary): Promise<void> {
    setBusy(true);
    setErr(null);
    try {
      await finalize(org.id);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  // Sign-in succeeded but the org's license is exhausted/lapsed → paywall.
  if (blockReason) {
    return (
      <BlockedScreen
        reason={blockReason}
        detail={humanize(blockReason)}
        orgRole={blockOrgRole ?? undefined}
        ownerEmail={blockOwnerEmail ?? undefined}
      />
    );
  }

  // Multi-club coach just signed in and needs to pick which club to open.
  if (orgChoice) {
    return (
      <div className="grid min-h-svh place-items-center bg-background p-6">
        <Glass level="floating" padding="lg" className="flex w-full max-w-md flex-col gap-5">
          <header className="text-center">
            <span className="text-label-caps uppercase tracking-widest text-pitch-lime">
              Coach Organizer
            </span>
            <h1 className="text-display-sm font-semibold">Verein auswählen</h1>
            <p className="mt-2 text-body-sm text-muted-foreground">
              Du gehörst zu mehreren Vereinen. Wähle aus, welcher auf diesem Gerät geöffnet werden
              soll — du kannst später über den Header wechseln.
            </p>
          </header>

          <ul className="flex flex-col gap-2">
            {orgChoice.map((o) => (
              <li key={o.id}>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => onPickOrg(o)}
                  className="flex w-full items-center justify-between gap-3 rounded-md border border-white/10 bg-white/2 p-3 text-left hover:border-pitch-lime/40 disabled:opacity-60"
                >
                  <div className="flex flex-col">
                    <span className="text-body-md font-medium">{o.name}</span>
                    <span className="text-body-sm text-muted-foreground">
                      {o.role === "owner" ? "Inhaber" : "Coach"} · {o.slug}
                    </span>
                  </div>
                  {o.logo ? (
                    <img src={o.logo} alt="" className="h-10 w-10 rounded-md object-cover" />
                  ) : (
                    <span className="grid h-10 w-10 place-items-center rounded-md bg-white/5 text-label-caps uppercase text-muted-foreground">
                      {o.name.slice(0, 2)}
                    </span>
                  )}
                </button>
              </li>
            ))}
          </ul>

          {err && (
            <p className="text-body-sm text-tactical-red" role="alert">
              {err}
            </p>
          )}

          <button
            type="button"
            onClick={() => {
              setOrgChoice(null);
              setLockedEmail(null);
              setPicker(accounts.length > 0);
            }}
            className="text-body-sm text-muted-foreground hover:text-foreground"
          >
            ← Anderes Konto verwenden
          </button>
        </Glass>
      </div>
    );
  }

  if (picker) {
    return (
      <div className="grid min-h-svh place-items-center bg-background p-6">
        <Glass level="floating" padding="lg" className="flex w-full max-w-lg flex-col gap-5">
          <header className="text-center">
            <span className="text-label-caps uppercase tracking-widest text-pitch-lime">
              Coach Organizer
            </span>
            <h1 className="text-display-sm font-semibold">Weiter als</h1>
            {state.kind === "blocked" && (
              <p className="mt-2 text-body-sm text-tactical-red">
                Sitzung abgelaufen ({state.reason ?? "unbekannt"}). Wähle ein Konto, um dich erneut
                anzumelden.
              </p>
            )}
            {state.kind !== "blocked" && (
              <p className="mt-2 text-body-sm text-muted-foreground">
                Wähle ein zuletzt verwendetes Konto — du wirst erneut nach dem Passwort gefragt.
              </p>
            )}
          </header>

          <ul className="flex flex-col gap-2">
            {accounts.map((a) => (
              <li
                key={a.email}
                className="flex items-center gap-3 rounded-md border border-white/10 bg-white/2 p-3"
              >
                <AccountAvatar name={a.name} email={a.email} image={a.image} />
                <button
                  type="button"
                  onClick={() => pickAccount(a)}
                  className="flex min-w-0 flex-1 flex-col items-start text-left"
                >
                  <span className="truncate text-body-md font-medium">{a.name ?? a.email}</span>
                  <span className="truncate text-body-sm text-muted-foreground">
                    {a.email}
                    {a.orgName ? ` · ${a.orgName}` : ""}
                  </span>
                </button>
                <button
                  type="button"
                  onClick={() => onForget(a)}
                  title="Konto auf diesem Gerät vergessen"
                  className="shrink-0 rounded-md border border-transparent px-2 py-1 text-label-caps uppercase text-muted-foreground hover:border-white/15 hover:text-tactical-red"
                >
                  Vergessen
                </button>
              </li>
            ))}
          </ul>

          <button
            type="button"
            onClick={useAnotherAccount}
            className="inline-flex h-11 items-center justify-center rounded-md border border-white/15 text-body-md font-medium hover:border-pitch-lime/60 hover:text-pitch-lime"
          >
            Anderes Konto verwenden
          </button>
        </Glass>
      </div>
    );
  }

  return (
    <div className="grid min-h-svh place-items-center bg-background p-6">
      <Glass level="floating" padding="lg" className="flex w-full max-w-md flex-col gap-5">
        <header className="text-center">
          <span className="text-label-caps uppercase tracking-widest text-pitch-lime">
            Coach Organizer
          </span>
          <h1 className="text-display-sm font-semibold">
            {lockedEmail ? "Willkommen zurück" : "Anmelden"}
          </h1>
          {state.kind === "blocked" && (
            <p className="mt-2 text-body-sm text-tactical-red">
              Sitzung abgelaufen ({state.reason ?? "unbekannt"}). Bitte erneut anmelden.
            </p>
          )}
          {state.kind !== "blocked" && !lockedEmail && (
            <p className="mt-2 text-body-sm text-muted-foreground">
              Verwende dieselbe E-Mail wie für dein Abo. Das Dashboard erreichst du unter{" "}
              <a
                href={import.meta.env.VITE_PUBLIC_LANDING_URL ?? "http://localhost:28781"}
                target="_blank"
                rel="noopener noreferrer"
                className="underline hover:text-foreground"
              >
                {import.meta.env.VITE_PUBLIC_LANDING_URL ?? "http://localhost:28781"}
              </a>
              .
            </p>
          )}
          {lockedEmail && (
            <p className="mt-2 text-body-sm text-muted-foreground">
              Passwort für {lockedEmail} eingeben.
            </p>
          )}
        </header>

        <form onSubmit={onSubmit} className="flex flex-col gap-3">
          <Field
            label="E-Mail"
            value={email}
            onChange={setEmail}
            type="email"
            autoComplete="email"
            required
            disabled={!!lockedEmail}
          />
          <Field
            label="Passwort"
            value={password}
            onChange={setPassword}
            type="password"
            autoComplete="current-password"
            required
            minLength={8}
          />

          {err && (
            <p role="alert" className="text-body-sm text-tactical-red">
              {err}
            </p>
          )}

          <button
            type="submit"
            disabled={busy}
            className="mt-1 inline-flex h-11 items-center justify-center rounded-md bg-pitch-lime text-body-md font-semibold text-[#1d3700] transition-shadow duration-fast ease-standard hover:shadow-glow disabled:opacity-60"
          >
            {busy ? "Bitte warten…" : "Anmelden & entsperren"}
          </button>
        </form>

        {accounts.length > 0 && (
          <button
            type="button"
            onClick={backToPicker}
            className="text-center text-body-sm text-muted-foreground hover:text-pitch-lime"
          >
            ← Zurück zu bekannten Konten
          </button>
        )}
        <p className="text-center text-body-sm text-muted-foreground">
          Noch kein Konto?{" "}
          <a
            href={import.meta.env.VITE_PUBLIC_LANDING_URL ?? "http://localhost:28781"}
            target="_blank"
            rel="noreferrer"
            className="text-pitch-lime hover:underline"
          >
            Im Dashboard registrieren
          </a>
          .
        </p>
      </Glass>
    </div>
  );
}

function Field(props: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  autoComplete?: string;
  required?: boolean;
  minLength?: number;
  disabled?: boolean;
}) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-label-caps uppercase text-muted-foreground">{props.label}</span>
      <input
        type={props.type ?? "text"}
        value={props.value}
        onChange={(e) => props.onChange(e.target.value)}
        autoComplete={props.autoComplete}
        required={props.required}
        minLength={props.minLength}
        disabled={props.disabled}
        className="h-11 rounded-md border border-white/15 bg-white/4 px-3 text-body-md outline-none focus:border-pitch-lime/60 focus:shadow-glow disabled:opacity-60"
      />
    </label>
  );
}

function humanize(reason: string): string {
  switch (reason) {
    case "no_organization":
      return "Konto hat keine Organisation — bitte den Support kontaktieren.";
    case "trial_exhausted":
      return "Dein Testzeitraum ist abgelaufen. Wähle einen Plan, um den Coach Organizer weiter zu nutzen.";
    case "subscription-blocked":
    case "subscription_blocked":
      return "Deine Subscription ist nicht aktiv. Aktualisiere deine Zahlung, um wieder Zugriff zu erhalten.";
    case "license-expired":
    case "expired":
      return "Dein Abo ist abgelaufen. Verlängere es im Dashboard.";
    default:
      return reason.replace(/[-_]/g, " ");
  }
}

/**
 * Issue-failure reasons that mean the *account* is valid but the *org* has no
 * usable license. These route to the paywall (`BlockedScreen`) instead of an
 * inline sign-in error.
 */
const BLOCKING_REASONS = new Set([
  "trial_exhausted",
  "subscription-blocked",
  "subscription_blocked",
  "license-expired",
  "expired",
  "http_402",
]);
